import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Chip,
  Avatar,
  Drawer,
  IconButton,
  InputAdornment,
  TextField,
  Collapse,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Search, Close, ExpandMore, ExpandLess } from '@mui/icons-material';
import { colors } from '../../theme/colors';
import { cricketFlagEmoji } from '../../utils/countryFlags';
import type {
  LeaderboardSnapshot,
  League,
  LeagueSquad,
  PlayerPool,
  PlayerPoolEntry,
} from '../../types/database';

// ─── Props ────────────────────────────────────────────────────────────────────

interface LeagueAnalyticsProps {
  snapshot: LeaderboardSnapshot;
  league: League;
  squads: LeagueSquad[];
  playerPool: PlayerPool | null;
}

// ─── Design tokens ────────────────────────────────────────────────────────────

const BG = '#0A1929';
const CARD_BG = alpha('#0d1f35', 0.95);
const CARD_BORDER = 'rgba(255,255,255,0.07)';
const DIVIDER = 'rgba(255,255,255,0.05)';
const TEXT_PRIMARY = '#ffffff';
const TEXT_SECONDARY = alpha('#fff', 0.5);
const TEXT_TERTIARY = alpha('#fff', 0.3);
const BLUE = colors.blue.electric; // #1E88E5
const GOLD = '#F59E0B';
const SILVER = alpha('#fff', 0.55);
const BRONZE = '#CD7F32';

// ─── Sub-component helpers ────────────────────────────────────────────────────

const card = {
  bgcolor: CARD_BG,
  border: `1px solid ${CARD_BORDER}`,
  borderRadius: '12px',
  boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
} as const;

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      sx={{
        fontSize: '0.65rem',
        textTransform: 'uppercase',
        letterSpacing: '0.1em',
        color: TEXT_TERTIARY,
        mb: 1.5,
      }}
    >
      {children}
    </Typography>
  );
}

function RoleBadge({ role }: { role: string }) {
  const map: Record<string, { label: string; color: string }> = {
    batsman:     { label: 'BAT', color: '#F59E0B' },
    bowler:      { label: 'BWL', color: BLUE },
    allrounder:  { label: 'AR',  color: '#22C55E' },
    wicketkeeper:{ label: 'WK',  color: '#A855F7' },
  };
  const entry = map[role];
  if (!entry) return null;
  return (
    <Typography
      sx={{
        fontSize: '0.6rem',
        fontWeight: 600,
        letterSpacing: '0.06em',
        color: alpha(entry.color, 0.65),
        textTransform: 'uppercase',
        lineHeight: 1,
      }}
    >
      {entry.label}
    </Typography>
  );
}

function RankMedal({ rank }: { rank: number }) {
  const color = rank === 1 ? GOLD : rank === 2 ? SILVER : rank === 3 ? BRONZE : TEXT_TERTIARY;
  return (
    <Typography
      sx={{
        fontSize: '0.75rem',
        fontWeight: 700,
        color,
        minWidth: 20,
        textAlign: 'center',
      }}
    >
      {rank}
    </Typography>
  );
}

function OwnershipBar({ pct }: { pct: number }) {
  return (
    <Box sx={{ width: 60, height: 4, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{ width: `${Math.min(pct, 100)}%`, height: '100%', bgcolor: BLUE, borderRadius: 2 }} />
    </Box>
  );
}

// ─── Computation helpers ──────────────────────────────────────────────────────

interface RunsEntry extends PlayerPoolEntry {
  totalRuns: number;
  innings: number;
  bestScore: number;
  totalBalls: number;
}

interface WicketsEntry extends PlayerPoolEntry {
  totalWickets: number;
  totalOvers: number;
  totalRunsConceded: number;
  spells: number;
}

interface DismissalsEntry extends PlayerPoolEntry {
  catches: number;
  runOuts: number;
  stumpings: number;
  total: number;
}

interface SREntry extends PlayerPoolEntry {
  totalRuns: number;
  totalBalls: number;
  strikeRate: number;
}

interface EconomyEntry extends PlayerPoolEntry {
  totalOvers: number;
  totalRunsConceded: number;
  totalWickets: number;
  economy: number;
}

function computeRunsLeaderboard(players: PlayerPoolEntry[]): RunsEntry[] {
  return players
    .map(p => ({
      ...p,
      totalRuns: (p.battingInnings ?? []).reduce((s, i) => s + i.runs, 0),
      innings: (p.battingInnings ?? []).length,
      bestScore: Math.max(0, ...(p.battingInnings ?? []).map(i => i.runs)),
      totalBalls: (p.battingInnings ?? []).reduce((s, i) => s + i.ballsFaced, 0),
    }))
    .filter(p => p.innings > 0)
    .sort((a, b) => b.totalRuns - a.totalRuns)
    .slice(0, 10);
}

function computeWicketsLeaderboard(players: PlayerPoolEntry[]): WicketsEntry[] {
  return players
    .map(p => ({
      ...p,
      totalWickets: (p.bowlingSpells ?? []).reduce((s, i) => s + i.wickets, 0),
      totalOvers: (p.bowlingSpells ?? []).reduce((s, i) => s + i.overs, 0),
      totalRunsConceded: (p.bowlingSpells ?? []).reduce((s, i) => s + i.runsConceded, 0),
      spells: (p.bowlingSpells ?? []).length,
    }))
    .filter(p => p.spells > 0)
    .sort((a, b) => b.totalWickets - a.totalWickets)
    .slice(0, 10);
}

function computeDismissalsLeaderboard(players: PlayerPoolEntry[]): DismissalsEntry[] {
  return players
    .map(p => ({
      ...p,
      catches: (p.fieldingPerformances ?? []).reduce((s, i) => s + i.catches, 0),
      runOuts: (p.fieldingPerformances ?? []).reduce((s, i) => s + i.runOuts, 0),
      stumpings: (p.fieldingPerformances ?? []).reduce((s, i) => s + i.stumpings, 0),
      total: (p.fieldingPerformances ?? []).reduce((s, i) => s + i.catches + i.runOuts + i.stumpings, 0),
    }))
    .filter(p => p.total > 0)
    .sort((a, b) => b.total - a.total)
    .slice(0, 10);
}

function computeSRLeaderboard(players: PlayerPoolEntry[]): SREntry[] {
  return players
    .map(p => ({
      ...p,
      totalRuns: (p.battingInnings ?? []).reduce((s, i) => s + i.runs, 0),
      totalBalls: (p.battingInnings ?? []).reduce((s, i) => s + i.ballsFaced, 0),
    }))
    .filter(p => p.totalBalls >= 20)
    .map(p => ({ ...p, strikeRate: (p.totalRuns / p.totalBalls) * 100 }))
    .sort((a, b) => b.strikeRate - a.strikeRate)
    .slice(0, 10);
}

function computeEconomyLeaderboard(players: PlayerPoolEntry[]): EconomyEntry[] {
  return players
    .map(p => ({
      ...p,
      totalOvers: (p.bowlingSpells ?? []).reduce((s, i) => s + i.overs, 0),
      totalRunsConceded: (p.bowlingSpells ?? []).reduce((s, i) => s + i.runsConceded, 0),
      totalWickets: (p.bowlingSpells ?? []).reduce((s, i) => s + i.wickets, 0),
    }))
    .filter(p => p.totalOvers >= 2)
    .map(p => ({ ...p, economy: p.totalRunsConceded / p.totalOvers }))
    .sort((a, b) => a.economy - b.economy)
    .slice(0, 10);
}

// ─── Player Profile Drawer ────────────────────────────────────────────────────

interface PlayerProfileProps {
  player: PlayerPoolEntry | null;
  onClose: () => void;
  ownershipPct: number;
}

function PlayerProfile({ player, onClose, ownershipPct }: PlayerProfileProps) {
  const [logsOpen, setLogsOpen] = useState(false);

  useEffect(() => {
    if (!player) setLogsOpen(false);
  }, [player]);

  if (!player) return null;

  const battingInnings = player.battingInnings ?? [];
  const bowlingSpells = player.bowlingSpells ?? [];
  const fieldingPerfs = player.fieldingPerformances ?? [];

  const totalRuns = battingInnings.reduce((s, i) => s + i.runs, 0);
  const totalBalls = battingInnings.reduce((s, i) => s + i.ballsFaced, 0);
  const bestScore = battingInnings.length > 0 ? Math.max(...battingInnings.map(i => i.runs)) : 0;
  const sr = totalBalls > 0 ? (totalRuns / totalBalls) * 100 : 0;

  const totalWickets = bowlingSpells.reduce((s, i) => s + i.wickets, 0);
  const totalOvers = bowlingSpells.reduce((s, i) => s + i.overs, 0);
  const totalRunsConceded = bowlingSpells.reduce((s, i) => s + i.runsConceded, 0);
  const economy = totalOvers > 0 ? totalRunsConceded / totalOvers : 0;

  const catches = fieldingPerfs.reduce((s, i) => s + i.catches, 0);
  const runOuts = fieldingPerfs.reduce((s, i) => s + i.runOuts, 0);
  const stumpings = fieldingPerfs.reduce((s, i) => s + i.stumpings, 0);

  const matchCount = Math.max(
    battingInnings.length,
    bowlingSpells.length,
    fieldingPerfs.length,
    1,
  );
  const ptsPerMatch = player.points / matchCount;

  // Gather all unique match labels for logs
  const allMatchLabels = Array.from(
    new Set([
      ...battingInnings.map(i => i.matchLabel ?? 'Unknown Match'),
      ...bowlingSpells.map(i => i.matchLabel ?? 'Unknown Match'),
      ...fieldingPerfs.map(i => i.matchLabel ?? 'Unknown Match'),
    ])
  );

  return (
    <Box sx={{ p: 2.5, bgcolor: BG, minHeight: 300 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: TEXT_PRIMARY, lineHeight: 1.2 }}>
            {cricketFlagEmoji(player.team)}{cricketFlagEmoji(player.team) ? ' ' : ''}
            {player.name}
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: TEXT_SECONDARY, mt: 0.25 }}>
            {player.team}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.75, mt: 0.75, alignItems: 'center' }}>
            <RoleBadge role={player.role} />
            <Typography sx={{ fontSize: '0.7rem', color: TEXT_TERTIARY }}>
              {ownershipPct.toFixed(0)}% owned
            </Typography>
          </Box>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography sx={{ fontSize: '1.75rem', fontWeight: 700, color: BLUE, lineHeight: 1 }}>
            {player.points.toFixed(0)}
          </Typography>
          <Typography sx={{ fontSize: '0.65rem', color: TEXT_TERTIARY }}>pts total</Typography>
        </Box>
      </Box>

      {/* Stat grids */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2 }}>
        {/* Fantasy */}
        <Box sx={{ ...card, p: 1.5 }}>
          <ProfileCardLabel>Fantasy Points</ProfileCardLabel>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <StatLine label="Total pts" value={player.points.toFixed(0)} />
            <StatLine label="Pts / match" value={ptsPerMatch.toFixed(1)} />
            <StatLine label="Ownership" value={`${ownershipPct.toFixed(0)}%`} />
          </Box>
        </Box>

        {/* Batting — only if has innings */}
        {battingInnings.length > 0 && (
          <Box sx={{ ...card, p: 1.5 }}>
            <ProfileCardLabel>Batting</ProfileCardLabel>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              <StatLine label="Runs" value={totalRuns} />
              <StatLine label="Innings" value={battingInnings.length} />
              <StatLine label="Best" value={bestScore} />
              {totalBalls >= 5 && <StatLine label="SR" value={sr.toFixed(1)} />}
            </Box>
          </Box>
        )}

        {/* Bowling — only if has spells */}
        {bowlingSpells.length > 0 && (
          <Box sx={{ ...card, p: 1.5 }}>
            <ProfileCardLabel>Bowling</ProfileCardLabel>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              <StatLine label="Wickets" value={totalWickets} />
              <StatLine label="Overs" value={totalOvers.toFixed(1)} />
              {totalOvers >= 1 && <StatLine label="Economy" value={economy.toFixed(2)} />}
            </Box>
          </Box>
        )}

        {/* Fielding — only if has records */}
        {fieldingPerfs.length > 0 && (
          <Box sx={{ ...card, p: 1.5 }}>
            <ProfileCardLabel>Fielding</ProfileCardLabel>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              <StatLine label="Catches" value={catches} />
              <StatLine label="Run-outs" value={runOuts} />
              <StatLine label="Stumpings" value={stumpings} />
            </Box>
          </Box>
        )}
      </Box>

      {/* Match logs toggle */}
      {allMatchLabels.length > 0 && (
        <Box>
          <Box
            onClick={() => setLogsOpen(v => !v)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              py: 1,
              borderTop: `1px solid ${DIVIDER}`,
            }}
          >
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: TEXT_SECONDARY }}>
              Match logs ({allMatchLabels.length})
            </Typography>
            {logsOpen ? <ExpandLess sx={{ color: TEXT_TERTIARY, fontSize: 18 }} /> : <ExpandMore sx={{ color: TEXT_TERTIARY, fontSize: 18 }} />}
          </Box>
          <Collapse in={logsOpen}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
              {allMatchLabels.map(label => {
                const bat = battingInnings.filter(i => (i.matchLabel ?? 'Unknown Match') === label);
                const bowl = bowlingSpells.filter(i => (i.matchLabel ?? 'Unknown Match') === label);
                const field = fieldingPerfs.filter(i => (i.matchLabel ?? 'Unknown Match') === label);
                return (
                  <Box key={label} sx={{ ...card, p: 1.5 }}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: TEXT_PRIMARY, mb: 0.75 }}>
                      {label}
                    </Typography>
                    {bat.map((i, idx) => (
                      <Typography key={idx} sx={{ fontSize: '0.7rem', color: TEXT_SECONDARY }}>
                        {i.runs} runs off {i.ballsFaced} balls · {i.pointsEarned.toFixed(0)} pts
                      </Typography>
                    ))}
                    {bowl.map((i, idx) => (
                      <Typography key={idx} sx={{ fontSize: '0.7rem', color: TEXT_SECONDARY }}>
                        {i.wickets}/{i.runsConceded} in {i.overs} ov · {i.pointsEarned.toFixed(0)} pts
                      </Typography>
                    ))}
                    {field.map((i, idx) => (
                      <Typography key={idx} sx={{ fontSize: '0.7rem', color: TEXT_SECONDARY }}>
                        {i.catches} Ct · {i.runOuts} RO · {i.stumpings} St · {i.pointsEarned.toFixed(0)} pts
                      </Typography>
                    ))}
                  </Box>
                );
              })}
            </Box>
          </Collapse>
        </Box>
      )}

      {allMatchLabels.length === 0 && (
        <Typography sx={{ fontSize: '0.75rem', color: TEXT_TERTIARY, textAlign: 'center', py: 2 }}>
          No match logs recorded yet
        </Typography>
      )}
    </Box>
  );
}

function ProfileCardLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      sx={{
        fontSize: '0.6rem',
        textTransform: 'uppercase',
        letterSpacing: '0.12em',
        fontWeight: 700,
        color: alpha('#fff', 0.55),
        mb: 1,
      }}
    >
      {children}
    </Typography>
  );
}

function StatLine({ label, value }: { label: string; value: string | number }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Typography sx={{ fontSize: '0.7rem', color: TEXT_TERTIARY }}>{label}</Typography>
      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: TEXT_PRIMARY }}>{value}</Typography>
    </Box>
  );
}

// ─── Player row (shared across Players tab leaderboards) ──────────────────────

function PlayerRow({
  rank,
  player,
  primary,
  secondary,
  onClick,
}: {
  rank: number;
  player: PlayerPoolEntry;
  primary: string;
  secondary: string;
  onClick?: () => void;
}) {
  const flag = cricketFlagEmoji(player.team);
  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        py: 1,
        borderBottom: `1px solid ${DIVIDER}`,
        cursor: onClick ? 'pointer' : 'default',
        '&:last-child': { borderBottom: 'none' },
        transition: 'background 0.15s',
        '&:hover': onClick ? { bgcolor: 'rgba(255,255,255,0.03)' } : undefined,
        borderRadius: 1,
        px: 0.5,
      }}
    >
      <RankMedal rank={rank} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: TEXT_PRIMARY, lineHeight: 1.2 }} noWrap>
          {flag}{flag ? ' ' : ''}{player.name}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
          <Typography sx={{ fontSize: '0.7rem', color: TEXT_SECONDARY }} noWrap>
            {player.team}
          </Typography>
          <RoleBadge role={player.role} />
        </Box>
      </Box>
      <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
        <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: TEXT_PRIMARY, lineHeight: 1 }}>
          {primary}
        </Typography>
        <Typography sx={{ fontSize: '0.65rem', color: TEXT_TERTIARY }}>
          {secondary}
        </Typography>
      </Box>
    </Box>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const LeagueAnalytics: React.FC<LeagueAnalyticsProps> = ({ snapshot, squads, playerPool }) => {
  const { standings } = snapshot;

  const [mainTab, setMainTab] = useState(0);
  const [playerSubTab, setPlayerSubTab] = useState(0);
  const [selectedPlayer, setSelectedPlayer] = useState<PlayerPoolEntry | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  if (!standings || standings.length === 0) return null;

  const totalManagers = standings.length;
  const avgPoints = standings.reduce((s, e) => s + e.totalPoints, 0) / totalManagers;
  const highestScore = Math.max(...standings.map(s => s.totalPoints));
  const lowestScore = Math.min(...standings.map(s => s.totalPoints));
  const pointsRange = highestScore - lowestScore;

  // Sorted standings
  const sorted = [...standings].sort((a, b) => b.totalPoints - a.totalPoints);
  const top10 = sorted.slice(0, 10);
  const bottom5 = sorted.slice(-5).reverse();

  // Player pool computations
  const players = playerPool?.players ?? [];
  const runsLb = computeRunsLeaderboard(players);
  const wicketsLb = computeWicketsLeaderboard(players);
  const dismissalsLb = computeDismissalsLeaderboard(players);
  const srLb = computeSRLeaderboard(players);
  const economyLb = computeEconomyLeaderboard(players);

  // Best innings / spell highlights
  const bestInnings = players
    .flatMap(p => (p.battingInnings ?? []).map(i => ({ ...i, playerName: p.name, team: p.team, role: p.role, player: p })))
    .sort((a, b) => b.runs - a.runs)[0];
  const bestSpell = players
    .flatMap(p => (p.bowlingSpells ?? []).map(i => ({ ...i, playerName: p.name, team: p.team, role: p.role, player: p })))
    .sort((a, b) => b.wickets - a.wickets || a.runsConceded - b.runsConceded)[0];

  // Points leaderboard (top 10 by total points)
  const pointsLb = [...players].sort((a, b) => b.points - a.points).slice(0, 10);

  // Ownership per player
  const ownershipMap = new Map<string, number>();
  squads.forEach(sq => sq.players.forEach(p => ownershipMap.set(p.playerId, (ownershipMap.get(p.playerId) ?? 0) + 1)));
  const ownershipPct = (playerId: string) =>
    totalManagers > 0 ? ((ownershipMap.get(playerId) ?? 0) / squads.length) * 100 : 0;

  // Team Intel computations
  const captainCounts = new Map<string, { name: string; count: number; points: number }>();
  const vcCounts = new Map<string, { name: string; count: number; points: number }>();
  const xfCounts = new Map<string, { name: string; count: number; points: number }>();
  standings.forEach(s => {
    if (s.captainId && s.captainName) {
      const ex = captainCounts.get(s.captainId);
      if (ex) { ex.count++; ex.points += s.captainPoints; }
      else { captainCounts.set(s.captainId, { name: s.captainName, count: 1, points: s.captainPoints }); }
    }
    if (s.viceCaptainId && s.viceCaptainName) {
      const ex = vcCounts.get(s.viceCaptainId);
      if (ex) { ex.count++; ex.points += s.viceCaptainPoints; }
      else { vcCounts.set(s.viceCaptainId, { name: s.viceCaptainName, count: 1, points: s.viceCaptainPoints }); }
    }
    if (s.xFactorId && s.xFactorName) {
      const ex = xfCounts.get(s.xFactorId);
      if (ex) { ex.count++; ex.points += s.xFactorPoints; }
      else { xfCounts.set(s.xFactorId, { name: s.xFactorName, count: 1, points: s.xFactorPoints }); }
    }
  });
  const topCaptain = Array.from(captainCounts.values()).sort((a, b) => b.count - a.count)[0];
  const topVC = Array.from(vcCounts.values()).sort((a, b) => b.count - a.count)[0];
  const topXF = Array.from(xfCounts.values()).sort((a, b) => b.count - a.count)[0];

  // Top picks overall (ownership)
  const playerOwnership = new Map<string, { name: string; count: number; role: string }>();
  squads.forEach(sq =>
    sq.players.forEach(p => {
      const ex = playerOwnership.get(p.playerId);
      if (ex) { ex.count++; }
      else { playerOwnership.set(p.playerId, { name: p.playerName, count: 1, role: p.role }); }
    })
  );
  const topPicks = Array.from(playerOwnership.entries())
    .map(([id, v]) => ({ ...v, playerId: id, pct: (v.count / squads.length) * 100 }))
    .sort((a, b) => b.pct - a.pct)
    .slice(0, 10);

  // Most unique squads
  const templateIds = new Set(topPicks.slice(0, 11).map(p => p.playerId));
  const uniqueSquads = squads
    .map(sq => {
      const uniqueCount = sq.players.filter(p => !templateIds.has(p.playerId)).length;
      const standing = standings.find(s => s.squadId === sq.id);
      return {
        displayName: standing?.displayName ?? sq.squadName,
        profilePicUrl: standing?.profilePicUrl,
        uniqueCount,
        uniquePct: (uniqueCount / sq.players.length) * 100,
        rank: sq.rank,
      };
    })
    .sort((a, b) => b.uniqueCount - a.uniqueCount)
    .slice(0, 5);

  // Transfer activity
  const transferActivity = squads
    .map(sq => {
      const standing = standings.find(s => s.squadId === sq.id);
      const total =
        (sq.benchTransfersUsed ?? 0) +
        (sq.flexibleTransfersUsed ?? 0) +
        (sq.midSeasonTransfersUsed ?? 0) +
        (sq.wildcardTransfersUsed ?? 0);
      return { displayName: standing?.displayName ?? sq.squadName, total };
    })
    .sort((a, b) => b.total - a.total)
    .slice(0, 3);

  // Search
  const searchResults =
    searchQuery.trim().length > 0
      ? players.filter(
          p =>
            p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            p.team.toLowerCase().includes(searchQuery.toLowerCase())
        )
      : [];

  // ── Player sub-tab definitions ─────────────────────────────────────────────
  const playerSubTabs = [
    { label: 'Points',  key: 'points' },
    { label: 'Runs',    key: 'runs' },
    { label: 'Wickets', key: 'wickets' },
    { label: 'Fielding',key: 'fielding' },
    { label: 'SR',      key: 'sr' },
    { label: 'Economy', key: 'economy' },
  ];

  // ── Shared tab style ───────────────────────────────────────────────────────
  const tabSx = {
    color: alpha('#fff', 0.45),
    fontWeight: 600,
    fontSize: '0.8rem',
    textTransform: 'none' as const,
    minHeight: 40,
    '&.Mui-selected': { color: TEXT_PRIMARY },
  };

  return (
    <Box sx={{ my: 4, bgcolor: BG }}>
      {/* ── Header ── */}
      <Typography
        sx={{
          fontSize: '1.1rem',
          fontWeight: 700,
          color: TEXT_PRIMARY,
          mb: 2,
          letterSpacing: '-0.01em',
        }}
      >
        Analytics
      </Typography>

      {/* ── Main Tabs ── */}
      <Tabs
        value={mainTab}
        onChange={(_, v) => setMainTab(v)}
        variant="scrollable"
        scrollButtons={false}
        sx={{
          mb: 3,
          borderBottom: `1px solid ${CARD_BORDER}`,
          '& .MuiTabs-indicator': { bgcolor: BLUE, height: 2 },
          minHeight: 40,
        }}
      >
        {['Overview', 'Players', 'Team Intel', 'Search'].map(label => (
          <Tab key={label} label={label} sx={tabSx} />
        ))}
      </Tabs>

      {/* ════════════════════════════════════════════════════════════
          TAB 0 — OVERVIEW
         ════════════════════════════════════════════════════════════ */}
      {mainTab === 0 && (
        <Box>
          {/* KPI strip */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1.5, mb: 3 }}>
            {[
              { label: 'Total Managers', value: totalManagers },
              { label: 'Highest Score',  value: highestScore.toFixed(1) },
              { label: 'League Avg',     value: avgPoints.toFixed(1) },
              { label: 'Points Range',   value: pointsRange.toFixed(1) },
            ].map(({ label, value }) => (
              <Box key={label} sx={{ ...card, p: 2 }}>
                <Typography sx={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: TEXT_TERTIARY, mb: 0.5 }}>
                  {label}
                </Typography>
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: TEXT_PRIMARY, lineHeight: 1 }}>
                  {value}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Rank Battles — Top 10 */}
          <Box sx={{ ...card, p: 2, mb: 3 }}>
            <SectionLabel>Rank Battles — Top 10</SectionLabel>
            {top10.map((s, idx) => {
              const next = top10[idx + 1];
              const lead = next ? s.totalPoints - next.totalPoints : 0;
              return (
                <Box
                  key={s.userId}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    py: 1,
                    borderBottom: idx < top10.length - 1 ? `1px solid ${DIVIDER}` : 'none',
                  }}
                >
                  <Chip
                    label={`#${s.rank}`}
                    size="small"
                    sx={{
                      bgcolor: s.rank === 1 ? alpha(GOLD, 0.2) : 'rgba(255,255,255,0.07)',
                      color: s.rank === 1 ? GOLD : TEXT_SECONDARY,
                      fontWeight: 700,
                      fontSize: '0.7rem',
                      height: 22,
                      minWidth: 36,
                      '& .MuiChip-label': { px: 0.75 },
                    }}
                  />
                  <Typography sx={{ flex: 1, fontSize: '0.875rem', fontWeight: 600, color: TEXT_PRIMARY }} noWrap>
                    {s.displayName}
                  </Typography>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: TEXT_SECONDARY, minWidth: 50, textAlign: 'right' }}>
                    {s.totalPoints.toFixed(1)}
                  </Typography>
                  {idx < top10.length - 1 && (
                    <Typography
                      sx={{
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        color: lead > 10 ? '#22C55E' : '#F59E0B',
                        minWidth: 40,
                        textAlign: 'right',
                      }}
                    >
                      +{lead.toFixed(1)}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>

          {/* Escape the Bottom — Bottom 5 */}
          {bottom5.length > 0 && (
            <Box sx={{ ...card, p: 2 }}>
              <SectionLabel>Escape the Bottom — Last 5</SectionLabel>
              {bottom5.map((s, idx) => {
                const above = sorted[sorted.length - bottom5.length + idx - 1];
                const needed = above ? above.totalPoints - s.totalPoints : 0;
                return (
                  <Box
                    key={s.userId}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      py: 1,
                      borderBottom: idx < bottom5.length - 1 ? `1px solid ${DIVIDER}` : 'none',
                    }}
                  >
                    <Chip
                      label={`#${s.rank}`}
                      size="small"
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.07)',
                        color: TEXT_SECONDARY,
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        height: 22,
                        minWidth: 36,
                        '& .MuiChip-label': { px: 0.75 },
                      }}
                    />
                    <Typography sx={{ flex: 1, fontSize: '0.875rem', fontWeight: 600, color: TEXT_PRIMARY }} noWrap>
                      {s.displayName}
                    </Typography>
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: TEXT_SECONDARY, minWidth: 50, textAlign: 'right' }}>
                      {s.totalPoints.toFixed(1)}
                    </Typography>
                    {needed > 0 && (
                      <Typography
                        sx={{
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          color: '#EF4444',
                          minWidth: 40,
                          textAlign: 'right',
                        }}
                      >
                        -{needed.toFixed(1)}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      )}

      {/* ════════════════════════════════════════════════════════════
          TAB 1 — PLAYERS
         ════════════════════════════════════════════════════════════ */}
      {mainTab === 1 && (
        <Box>
          {/* Highlight cards */}
          {(bestInnings || bestSpell) && (
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 3 }}>
              {bestInnings && (
                <Box sx={{ ...card, p: 1.75 }}>
                  <SectionLabel>Best Innings</SectionLabel>
                  <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: BLUE, lineHeight: 1 }}>
                    {bestInnings.runs}
                  </Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: TEXT_SECONDARY, mt: 0.25 }} noWrap>
                    {cricketFlagEmoji(bestInnings.team)}{cricketFlagEmoji(bestInnings.team) ? ' ' : ''}{bestInnings.playerName}
                  </Typography>
                  <Typography sx={{ fontSize: '0.65rem', color: TEXT_TERTIARY }}>
                    {bestInnings.matchLabel ?? ''}
                  </Typography>
                </Box>
              )}
              {bestSpell && (
                <Box sx={{ ...card, p: 1.75 }}>
                  <SectionLabel>Best Spell</SectionLabel>
                  <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: BLUE, lineHeight: 1 }}>
                    {bestSpell.wickets}/{bestSpell.runsConceded}
                  </Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: TEXT_SECONDARY, mt: 0.25 }} noWrap>
                    {cricketFlagEmoji(bestSpell.team)}{cricketFlagEmoji(bestSpell.team) ? ' ' : ''}{bestSpell.playerName}
                  </Typography>
                  <Typography sx={{ fontSize: '0.65rem', color: TEXT_TERTIARY }}>
                    {bestSpell.overs} ov · {bestSpell.matchLabel ?? ''}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Sub-tab pills */}
          <Typography
            sx={{
              fontSize: '0.6rem',
              fontWeight: 700,
              letterSpacing: '0.12em',
              color: TEXT_TERTIARY,
              textTransform: 'uppercase',
              mb: 1,
            }}
          >
            Top 10
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: 0.75,
              overflowX: 'auto',
              mb: 2,
              pb: 0.5,
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            {playerSubTabs.map((t, i) => (
              <Chip
                key={t.key}
                label={t.label}
                onClick={() => setPlayerSubTab(i)}
                sx={{
                  bgcolor: playerSubTab === i ? BLUE : 'rgba(255,255,255,0.07)',
                  color: playerSubTab === i ? '#fff' : TEXT_SECONDARY,
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  height: 28,
                  flexShrink: 0,
                  cursor: 'pointer',
                  transition: 'background 0.15s',
                  '& .MuiChip-label': { px: 1.25 },
                  '&:hover': { bgcolor: playerSubTab === i ? BLUE : 'rgba(255,255,255,0.12)' },
                }}
              />
            ))}
          </Box>

          {/* Sub-tab content */}
          <Box sx={{ ...card, p: 2 }}>
            {/* Points */}
            {playerSubTab === 0 && (
              <>
                <SectionLabel>Top 10 — Fantasy Points</SectionLabel>
                {pointsLb.length === 0 ? (
                  <EmptyState msg="No player data yet" />
                ) : (
                  pointsLb.map((p, i) => (
                    <PlayerRow
                      key={p.playerId}
                      rank={i + 1}
                      player={p}
                      primary={p.points.toFixed(0)}
                      secondary={`${ownershipPct(p.playerId).toFixed(0)}% owned`}
                      onClick={() => setSelectedPlayer(p)}
                    />
                  ))
                )}
              </>
            )}

            {/* Runs */}
            {playerSubTab === 1 && (
              <>
                <SectionLabel>Top 10 — Most Runs</SectionLabel>
                {runsLb.length === 0 ? (
                  <EmptyState msg="No batting data yet" />
                ) : (
                  runsLb.map((p, i) => (
                    <PlayerRow
                      key={p.playerId}
                      rank={i + 1}
                      player={p}
                      primary={String(p.totalRuns)}
                      secondary={`${p.innings} inns · best ${p.bestScore}`}
                      onClick={() => setSelectedPlayer(p)}
                    />
                  ))
                )}
              </>
            )}

            {/* Wickets */}
            {playerSubTab === 2 && (
              <>
                <SectionLabel>Top 10 — Most Wickets</SectionLabel>
                {wicketsLb.length === 0 ? (
                  <EmptyState msg="No bowling data yet" />
                ) : (
                  wicketsLb.map((p, i) => (
                    <PlayerRow
                      key={p.playerId}
                      rank={i + 1}
                      player={p}
                      primary={String(p.totalWickets)}
                      secondary={`${p.totalOvers.toFixed(1)} ov · econ ${p.totalOvers > 0 ? (p.totalRunsConceded / p.totalOvers).toFixed(2) : '—'}`}
                      onClick={() => setSelectedPlayer(p)}
                    />
                  ))
                )}
              </>
            )}

            {/* Fielding */}
            {playerSubTab === 3 && (
              <>
                <SectionLabel>Top 10 — Fielding Dismissals</SectionLabel>
                {dismissalsLb.length === 0 ? (
                  <EmptyState msg="No fielding data yet" />
                ) : (
                  dismissalsLb.map((p, i) => (
                    <PlayerRow
                      key={p.playerId}
                      rank={i + 1}
                      player={p}
                      primary={String(p.total)}
                      secondary={`${p.catches} Ct · ${p.runOuts} RO · ${p.stumpings} St`}
                      onClick={() => setSelectedPlayer(p)}
                    />
                  ))
                )}
              </>
            )}

            {/* Strike Rate */}
            {playerSubTab === 4 && (
              <>
                <SectionLabel>Top 10 — Batting Strike Rate (min 20 balls)</SectionLabel>
                {srLb.length === 0 ? (
                  <EmptyState msg="No players with 20+ balls faced yet" />
                ) : (
                  srLb.map((p, i) => (
                    <PlayerRow
                      key={p.playerId}
                      rank={i + 1}
                      player={p}
                      primary={p.strikeRate.toFixed(1)}
                      secondary={`${p.totalRuns} runs off ${p.totalBalls} balls`}
                      onClick={() => setSelectedPlayer(p)}
                    />
                  ))
                )}
              </>
            )}

            {/* Economy */}
            {playerSubTab === 5 && (
              <>
                <SectionLabel>Top 10 — Bowling Economy (min 2 overs)</SectionLabel>
                {economyLb.length === 0 ? (
                  <EmptyState msg="No players with 2+ overs bowled yet" />
                ) : (
                  economyLb.map((p, i) => (
                    <PlayerRow
                      key={p.playerId}
                      rank={i + 1}
                      player={p}
                      primary={p.economy.toFixed(2)}
                      secondary={`${p.totalOvers.toFixed(1)} ov · ${p.totalWickets}w`}
                      onClick={() => setSelectedPlayer(p)}
                    />
                  ))
                )}
              </>
            )}
          </Box>
        </Box>
      )}

      {/* ════════════════════════════════════════════════════════════
          TAB 2 — TEAM INTEL
         ════════════════════════════════════════════════════════════ */}
      {mainTab === 2 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Popular Picks (C / VC / X-Factor) */}
          <Box sx={{ ...card, p: 2 }}>
            <SectionLabel>Popular Role Picks</SectionLabel>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {topCaptain && (
                <RolePickRow
                  roleLabel="C"
                  roleColor={GOLD}
                  name={topCaptain.name}
                  count={topCaptain.count}
                  total={squads.length}
                  pts={topCaptain.points}
                />
              )}
              {topVC && (
                <RolePickRow
                  roleLabel="VC"
                  roleColor="#A855F7"
                  name={topVC.name}
                  count={topVC.count}
                  total={squads.length}
                  pts={topVC.points}
                />
              )}
              {topXF && (
                <RolePickRow
                  roleLabel="X"
                  roleColor="#22C55E"
                  name={topXF.name}
                  count={topXF.count}
                  total={squads.length}
                  pts={topXF.points}
                />
              )}
              {!topCaptain && !topVC && !topXF && <EmptyState msg="No role data yet" />}
            </Box>
          </Box>

          {/* Top Picks */}
          <Box sx={{ ...card, p: 2 }}>
            <SectionLabel>Top Picks — Ownership</SectionLabel>
            {topPicks.length === 0 ? (
              <EmptyState msg="No squad data yet" />
            ) : (
              topPicks.map((p, i) => (
                <Box
                  key={p.playerId}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    py: 1,
                    borderBottom: i < topPicks.length - 1 ? `1px solid ${DIVIDER}` : 'none',
                  }}
                >
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: TEXT_TERTIARY, minWidth: 20, textAlign: 'center' }}>
                    {i + 1}
                  </Typography>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: TEXT_PRIMARY }} noWrap>
                      {p.name}
                    </Typography>
                    <RoleBadge role={p.role} />
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: TEXT_PRIMARY }}>
                      {p.pct.toFixed(0)}%
                    </Typography>
                    <OwnershipBar pct={p.pct} />
                  </Box>
                </Box>
              ))
            )}
          </Box>

          {/* Most Unique Squads */}
          {uniqueSquads.length > 0 && (
            <Box sx={{ ...card, p: 2 }}>
              <SectionLabel>Most Unique Squads</SectionLabel>
              {uniqueSquads.map((sq, i) => (
                <Box
                  key={i}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    py: 1,
                    borderBottom: i < uniqueSquads.length - 1 ? `1px solid ${DIVIDER}` : 'none',
                  }}
                >
                  <Avatar src={sq.profilePicUrl} sx={{ width: 28, height: 28, bgcolor: 'rgba(255,255,255,0.1)' }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: TEXT_PRIMARY }} noWrap>
                      {sq.displayName}
                    </Typography>
                    <Typography sx={{ fontSize: '0.65rem', color: TEXT_TERTIARY }}>
                      {sq.uniqueCount} unique picks · Rank #{sq.rank}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: BLUE }}>
                    {sq.uniquePct.toFixed(0)}%
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {/* Transfer Activity */}
          {transferActivity.some(t => t.total > 0) && (
            <Box sx={{ ...card, p: 2 }}>
              <SectionLabel>Most Active Managers (Transfers)</SectionLabel>
              {transferActivity.filter(t => t.total > 0).map((t, i) => (
                <Box
                  key={i}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    py: 1,
                    borderBottom: i < transferActivity.length - 1 ? `1px solid ${DIVIDER}` : 'none',
                  }}
                >
                  <RankMedal rank={i + 1} />
                  <Typography sx={{ flex: 1, fontSize: '0.875rem', fontWeight: 600, color: TEXT_PRIMARY }} noWrap>
                    {t.displayName}
                  </Typography>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: TEXT_SECONDARY }}>
                    {t.total} transfers
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* ════════════════════════════════════════════════════════════
          TAB 3 — SEARCH
         ════════════════════════════════════════════════════════════ */}
      {mainTab === 3 && (
        <Box>
          <TextField
            inputRef={searchInputRef}
            fullWidth
            placeholder="Search by name or team…"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: TEXT_TERTIARY, fontSize: 18 }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchQuery('')} sx={{ color: TEXT_TERTIARY }}>
                    <Close fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                bgcolor: CARD_BG,
                color: TEXT_PRIMARY,
                borderRadius: '10px',
                '& fieldset': { borderColor: CARD_BORDER },
                '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                '&.Mui-focused fieldset': { borderColor: BLUE },
              },
              '& input::placeholder': { color: TEXT_TERTIARY },
            }}
          />

          {searchQuery.trim().length === 0 && (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography sx={{ fontSize: '0.875rem', color: TEXT_TERTIARY }}>
                Start typing to search players
              </Typography>
            </Box>
          )}

          {searchQuery.trim().length > 0 && searchResults.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography sx={{ fontSize: '0.875rem', color: TEXT_TERTIARY }}>
                No players found for "{searchQuery}"
              </Typography>
            </Box>
          )}

          {searchResults.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {searchResults.map(p => {
                const flag = cricketFlagEmoji(p.team);
                const pct = ownershipPct(p.playerId);
                return (
                  <Box
                    key={p.playerId}
                    onClick={() => setSelectedPlayer(p)}
                    sx={{
                      ...card,
                      p: 1.75,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      cursor: 'pointer',
                      transition: 'border-color 0.15s',
                      '&:hover': { borderColor: alpha(BLUE, 0.4) },
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: TEXT_PRIMARY }} noWrap>
                        {flag}{flag ? ' ' : ''}{p.name}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
                        <Typography sx={{ fontSize: '0.7rem', color: TEXT_SECONDARY }} noWrap>
                          {p.team}
                        </Typography>
                        <RoleBadge role={p.role} />
                      </Box>
                    </Box>
                    <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                      <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: BLUE }}>
                        {p.points.toFixed(0)}
                      </Typography>
                      <Typography sx={{ fontSize: '0.65rem', color: TEXT_TERTIARY }}>
                        {pct.toFixed(0)}% owned
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      )}

      {/* ── Player Profile Drawer ── */}
      <Drawer
        anchor="bottom"
        open={selectedPlayer !== null}
        onClose={() => setSelectedPlayer(null)}
        PaperProps={{
          sx: {
            bgcolor: BG,
            borderTop: `1px solid ${CARD_BORDER}`,
            borderRadius: '16px 16px 0 0',
            maxHeight: '85vh',
            overflowY: 'auto',
          },
        }}
      >
        {/* Drag handle */}
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1.5, pb: 0.5 }}>
          <Box sx={{ width: 36, height: 4, bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2 }} />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, pb: 0.5 }}>
          <IconButton size="small" onClick={() => setSelectedPlayer(null)} sx={{ color: TEXT_TERTIARY }}>
            <Close fontSize="small" />
          </IconButton>
        </Box>
        <PlayerProfile
          player={selectedPlayer}
          onClose={() => setSelectedPlayer(null)}
          ownershipPct={selectedPlayer ? ownershipPct(selectedPlayer.playerId) : 0}
        />
      </Drawer>
    </Box>
  );
};

// ─── Small helpers ────────────────────────────────────────────────────────────

function EmptyState({ msg }: { msg: string }) {
  return (
    <Typography sx={{ fontSize: '0.8rem', color: TEXT_TERTIARY, textAlign: 'center', py: 3 }}>
      {msg}
    </Typography>
  );
}

function RolePickRow({
  roleLabel,
  roleColor,
  name,
  count,
  total,
  pts,
}: {
  roleLabel: string;
  roleColor: string;
  name: string;
  count: number;
  total: number;
  pts: number;
}) {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Chip
        label={roleLabel}
        size="small"
        sx={{
          height: 22,
          fontSize: '0.65rem',
          fontWeight: 700,
          bgcolor: alpha(roleColor, 0.15),
          color: roleColor,
          border: `1px solid ${alpha(roleColor, 0.3)}`,
          minWidth: 28,
          flexShrink: 0,
          '& .MuiChip-label': { px: 0.75 },
        }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: TEXT_PRIMARY }} noWrap>
          {name}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
          <OwnershipBar pct={pct} />
          <Typography sx={{ fontSize: '0.65rem', color: TEXT_TERTIARY }}>
            {pct.toFixed(0)}%
          </Typography>
        </Box>
      </Box>
      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: TEXT_SECONDARY, flexShrink: 0 }}>
        {pts.toFixed(0)} pts
      </Typography>
    </Box>
  );
}

export default LeagueAnalytics;
